import { cn } from './internal/utils'
import { BORDER, RADIUS_CARD, SURFACE } from './internal/presets'

const SIZE_PADDING = {
  default: 'p-3',
  small: 'p-2',
}

const CardMeta = ({ avatar, title, description, className, ...rest }) => (
  <div className={cn('ui-card-meta flex items-start gap-3', className)} {...rest}>
    {avatar ? <div className="ui-card-meta-avatar shrink-0">{avatar}</div> : null}
    <div className="ui-card-meta-detail min-w-0 flex-1">
      {title ? (
        <div className="ui-card-meta-title truncate font-semibold text-[var(--text-dark)]">
          {title}
        </div>
      ) : null}
      {description ? (
        <div className="ui-card-meta-description text-sm text-[var(--text-muted)]">
          {description}
        </div>
      ) : null}
    </div>
  </div>
)

const CardGrid = ({ className, children, ...rest }) => (
  <div
    className={cn('ui-card-grid border border-[var(--border-subtle)] p-3', className)}
    {...rest}
  >
    {children}
  </div>
)

/**
 * antd Card. Supports title, extra, actions, cover, tabList, bordered, hoverable,
 * loading, size, and the `headStyle`/`bodyStyle`/`styles` escape hatches.
 */
const Card = ({
  title,
  extra,
  actions,
  cover,
  bordered = true,
  hoverable = false,
  loading = false,
  size = 'default',
  className,
  style,
  headStyle,
  bodyStyle,
  styles,
  tabList,
  activeTabKey,
  onTabChange,
  children,
  ...rest
}) => {
  const resolvedHeadStyle = styles?.header || headStyle
  const resolvedBodyStyle = styles?.body || bodyStyle
  const hasHead = Boolean(title || extra || tabList?.length)

  return (
    <div
      className={cn(
        'ui-card',
        SURFACE,
        // The reference's Card is `rounded-xl border bg-card shadow`.
        RADIUS_CARD,
        'shadow-[var(--shadow-sm)]',
        bordered && `border ${BORDER}`,
        hoverable &&
          'transition-shadow duration-[var(--duration-normal)] hover:shadow-[var(--shadow-card)]',
        size === 'small' && 'ui-card-small',
        className
      )}
      style={style}
      {...rest}
    >
      {hasHead ? (
        <div
          className={cn(
            'ui-card-head flex items-center justify-between gap-3 border-b px-3',
            BORDER,
            size === 'small' ? 'min-h-[38px] py-2' : 'min-h-[48px] py-2.5'
          )}
          style={resolvedHeadStyle}
        >
          <div className="ui-card-head-title min-w-0 flex-1 truncate font-semibold text-[var(--text-dark)]">
            {title}
          </div>
          {extra ? <div className="ui-card-extra shrink-0">{extra}</div> : null}
        </div>
      ) : null}

      {tabList?.length ? (
        <div className={cn('ui-card-head-tabs flex gap-1 border-b px-2', BORDER)}>
          {tabList.map((tab) => {
            const isActive = tab.key === activeTabKey
            return (
              <button
                key={tab.key}
                type="button"
                aria-current={isActive || undefined}
                onClick={() => onTabChange?.(tab.key)}
                className={cn(
                  'min-h-[var(--control-height)] border-b-2 px-3 text-sm font-medium',
                  isActive
                    ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-body)]'
                )}
              >
                {tab.tab ?? tab.label}
              </button>
            )
          })}
        </div>
      ) : null}

      {cover ? <div className="ui-card-cover overflow-hidden">{cover}</div> : null}

      <div
        className={cn('ui-card-body', SIZE_PADDING[size] || SIZE_PADDING.default)}
        style={resolvedBodyStyle}
      >
        {loading ? (
          <div className="ui-card-loading-content space-y-2" aria-busy="true">
            <div className="h-3 w-2/5 animate-pulse rounded bg-[var(--gray-300)]" />
            <div className="h-3 w-full animate-pulse rounded bg-[var(--gray-300)]" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-[var(--gray-300)]" />
          </div>
        ) : (
          children
        )}
      </div>

      {actions?.length ? (
        <ul className={cn('ui-card-actions flex list-none border-t p-0', BORDER)}>
          {actions.map((action, index) => (
            // eslint-disable-next-line react/no-array-index-key -- antd actions are positional
            <li key={index} className="flex flex-1 items-center justify-center py-2">
              {action}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

Card.Meta = CardMeta
Card.Grid = CardGrid

export default Card
